/**
 * PinnedRail — rail horizontal de fixados no topo do popover condensado
 * (Mensageria Condensada · superfície 5 · PR F1a).
 *
 * Lista pessoas/threads fixados via `usePins()`. O backend já resolve um `label`
 * por pin (nome da contraparte no 1:1 / nome no pin `user` / "Canal do time" no
 * grupo) via `User.name` — favoritos distintos não colidem mais num genérico, e
 * não dependemos da presença (incompleta). Avatar: `counterpartUserId` presente →
 * iniciais coloridas por usuário; grupo (null) → glyph de time. Clicar num pin de
 * thread abre aquele thread inline. A affordance "Fixar" é mínima nesta fatia (o
 * star do header do thread é o caminho principal; o picker rico é deferido p/ F1b).
 *
 * Sem pins → o rail não renderiza (sem CTA falso, estado honesto).
 */

import { Plus, Users } from 'lucide-react';

import { avatarColorFor, initialsOf } from '../../../../lib/format';
import { usePins } from '../hooks/use-pins';

import type { PinSummary } from '../../types';

interface PinnedRailProps {
  /** Abre um thread fixado inline. */
  onOpenThread: (threadId: string) => void;
}

export function PinnedRail({ onOpenThread }: PinnedRailProps): JSX.Element | null {
  const { pins } = usePins();

  if (pins.length === 0) return null;

  return (
    <div className="mc-section mc-section--divider">
      <div className="mc-eyebrow">Fixados</div>
      <div className="mc-pin-rail">
        {pins.map((pin) => (
          <PinButton key={pin.id} pin={pin} onOpenThread={onOpenThread} />
        ))}
        {/* Affordance "Fixar" — mínima nesta fatia (star do thread é o caminho principal). */}
        <button type="button" className="mc-pin mc-pin--add" title="Fixar — em breve" disabled>
          <span className="mc-pin-avatar mc-pin-avatar--add" aria-hidden>
            <Plus size={15} strokeWidth={1.5} />
          </span>
          <span className="mc-pin-name">Fixar</span>
        </button>
      </div>
    </div>
  );
}

/** Fallback honesto enquanto o pin otimista não tem `label` resolvido (pré-refetch). */
function pinLabel(pin: PinSummary): string {
  if (pin.label.length > 0) return pin.label;
  return pin.targetType === 'user' ? 'Pessoa fixada' : 'Conversa fixada';
}

function PinButton({
  pin,
  onOpenThread,
}: {
  pin: PinSummary;
  onOpenThread: (threadId: string) => void;
}): JSX.Element {
  const isThread = pin.targetType === 'thread';
  const label = pinLabel(pin);

  return (
    <button
      type="button"
      className="mc-pin"
      title={label}
      // Pin de thread abre o thread; pin de pessoa (iniciar nova conversa) é F1b.
      disabled={!isThread}
      onClick={() => {
        if (isThread) onOpenThread(pin.targetId);
      }}
    >
      {pin.counterpartUserId !== null ? (
        <span
          className="mc-pin-avatar"
          aria-hidden
          style={{ background: avatarColorFor(pin.counterpartUserId) }}
        >
          {initialsOf(label)}
        </span>
      ) : (
        <span className="mc-pin-avatar" aria-hidden>
          <Users size={16} strokeWidth={1.6} />
        </span>
      )}
      <span className="mc-pin-name">{label}</span>
    </button>
  );
}
